import type { ReactElement, ReactNode, Ref } from "react";
import {
  Children,
  cloneElement,
  isValidElement,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";

import type { ThemeContextProps } from "@/react/providers/Theme";
import { ThemeContext } from "@/react/providers/Theme";
import { APPEARANCE } from "@/react/providers/Theme";
import { updateTheme } from "@/react/providers/Theme/updateTheme";

export interface ScopedAppearanceThemeProviderProps {
  appearance: (typeof APPEARANCE)[keyof typeof APPEARANCE];
  children: ReactNode;
}

export const ScopedThemeProvider = ({
  appearance,
  children,
}: ScopedAppearanceThemeProviderProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    updateTheme({
      theme: { current_appearance: appearance },
      documentElement: element,
      selector: "",
    });
    // Compass defines its dark tokens on the root element only, so a descendant
    // cannot return to light just by dropping `dark`: it keeps inheriting the app's
    // dark values. Its `.light` class re-declares them on any element, which is what
    // keeps a light region light when the surrounding app is dark.
    element.classList.toggle("light", appearance === APPEARANCE.LIGHT);
  }, [appearance]);

  const value = useMemo<ThemeContextProps>(
    () => ({
      brandColor: { light: "", dark: "" },
      brandTextColor: { light: "", dark: "" },
      currentAppearance: appearance,
      currentTheme: "default",
      customColors: {},
      isCustomTheme: false,
      isDarkAppearance: appearance === APPEARANCE.DARK,
      isLightAppearance: appearance === APPEARANCE.LIGHT,
    }),
    [appearance],
  );

  const child = Children.only(children);
  const content = isValidElement(child)
    ? cloneElement(child as ReactElement<{ ref?: Ref<HTMLDivElement> }>, {
        ref: containerRef,
      })
    : children;

  return <ThemeContext.Provider value={value}>{content}</ThemeContext.Provider>;
};
